import type { CategoryExecutor } from './executor.types';

export const executeApplicantNode: CategoryExecutor = async (node, context) => {
  const type = node.nodeType.toLowerCase();
  const config = node.configuration ?? {};

  if (
    type.endsWith('.select-applicant-type') ||
    type.endsWith('.select-customer-status')
  ) {
    return { status: 'pause' };
  }

  try {
    const result = await context.proxy.invoke({
      category: 'applicant',
      operation: type.split('.').pop() ?? 'applicant',
      configuration: config,
      body: context.payload,
    });
    return { status: 'success', output: { applicantResult: result } };
  } catch (error) {
    return {
      status: 'failure',
      message: error instanceof Error ? error.message : 'Applicant operation failed',
      code: 'APPLICANT_FAILED',
    };
  }
};

export const executeCustomerNode: CategoryExecutor = async (node, context) => {
  const type = node.nodeType.toLowerCase();
  const config = node.configuration ?? {};
  try {
    const result = await context.proxy.invoke({
      category: 'customer',
      operation: type.split('.').pop() ?? 'customer',
      configuration: config,
      body: context.payload,
    });
    return { status: 'success', output: { customerResult: result } };
  } catch (error) {
    return {
      status: 'failure',
      message: error instanceof Error ? error.message : 'Customer operation failed',
      code: 'CUSTOMER_FAILED',
    };
  }
};

export const executeDocumentsNode: CategoryExecutor = async (node, context) => {
  const type = node.nodeType.toLowerCase();
  if (type.endsWith('.upload-document') || type.endsWith('.scan-document')) {
    return { status: 'pause' };
  }
  try {
    const result = await context.proxy.invoke({
      category: 'documents',
      operation: type.split('.').pop() ?? 'documents',
      configuration: node.configuration ?? {},
      body: context.payload,
    });
    return { status: 'success', output: { documentResult: result } };
  } catch (error) {
    return {
      status: 'failure',
      message: error instanceof Error ? error.message : 'Document operation failed',
      code: 'DOCUMENTS_FAILED',
    };
  }
};

export const executeVerificationNode: CategoryExecutor = async (node, context) => {
  try {
    const result = await context.proxy.invoke({
      category: 'verification',
      operation: node.nodeType.split('.').pop() ?? 'verify',
      configuration: node.configuration ?? {},
      body: context.payload,
    });
    return { status: 'success', output: { verificationResult: result } };
  } catch (error) {
    return {
      status: 'failure',
      message: error instanceof Error ? error.message : 'Verification failed',
      code: 'VERIFICATION_FAILED',
    };
  }
};

export const executeIntegrationsNode: CategoryExecutor = async (node, context) => {
  const config = node.configuration ?? {};
  try {
    const result = await context.proxy.invoke({
      category: 'integrations',
      operation: node.nodeType.split('.').pop() ?? 'invoke',
      endpoint: typeof config['endpoint'] === 'string' ? config['endpoint'] : undefined,
      method: typeof config['method'] === 'string' ? config['method'] : 'POST',
      configuration: config,
      body: context.payload,
    });
    return { status: 'success', output: { integrationResult: result } };
  } catch (error) {
    return {
      status: 'failure',
      message: error instanceof Error ? error.message : 'Integration failed',
      code: 'INTEGRATION_FAILED',
    };
  }
};

export const executeAiNode: CategoryExecutor = async (node, context) => {
  try {
    const result = await context.proxy.invoke({
      category: 'ai',
      operation: node.nodeType.split('.').pop() ?? 'ai',
      configuration: node.configuration ?? {},
      body: context.payload,
    });
    return { status: 'success', output: { aiResult: result } };
  } catch (error) {
    return {
      status: 'failure',
      message: error instanceof Error ? error.message : 'AI operation failed',
      code: 'AI_FAILED',
    };
  }
};

export const executeApprovalNode: CategoryExecutor = () => ({ status: 'pause' });

export const executeTasksNode: CategoryExecutor = async (node, context) => {
  const type = node.nodeType.toLowerCase();
  if (
    type.includes('create-task') ||
    type.includes('assign-task') ||
    type.includes('checklist') ||
    type.includes('comment') ||
    type.includes('audit-note')
  ) {
    return { status: 'pause' };
  }
  try {
    const result = await context.proxy.invoke({
      category: 'tasks',
      operation: type.split('.').pop() ?? 'task',
      configuration: node.configuration ?? {},
      body: context.payload,
    });
    return { status: 'success', output: { taskResult: result } };
  } catch (error) {
    return {
      status: 'failure',
      message: error instanceof Error ? error.message : 'Task operation failed',
    };
  }
};

export const executeCommunicationNode: CategoryExecutor = async (node, context) => {
  try {
    const result = await context.proxy.invoke({
      category: 'communication',
      operation: node.nodeType.split('.').pop() ?? 'send',
      configuration: node.configuration ?? {},
      body: context.payload,
    });
    return { status: 'success', output: { communicationResult: result } };
  } catch (error) {
    return {
      status: 'failure',
      message: error instanceof Error ? error.message : 'Communication failed',
    };
  }
};

export const executeStorageNode: CategoryExecutor = async (node, context) => {
  try {
    const result = await context.proxy.invoke({
      category: 'storage',
      operation: node.nodeType.split('.').pop() ?? 'storage',
      configuration: node.configuration ?? {},
      body: context.payload,
    });
    return { status: 'success', output: { storageResult: result } };
  } catch (error) {
    return {
      status: 'failure',
      message: error instanceof Error ? error.message : 'Storage operation failed',
    };
  }
};

export const executeUtilityNode: CategoryExecutor = (node, context) => {
  const type = node.nodeType.toLowerCase();
  const config = node.configuration ?? {};

  if (type.endsWith('.date')) {
    const format = typeof config['format'] === 'string' ? config['format'] : 'ISO';
    return {
      status: 'success',
      output: { date: format === 'ISO' ? new Date().toISOString() : new Date().toLocaleDateString() },
    };
  }
  if (type.endsWith('.time')) {
    return { status: 'success', output: { time: new Date().toISOString() } };
  }
  if (type.endsWith('.uuid')) {
    return { status: 'success', output: { uuid: crypto.randomUUID() } };
  }
  if (type.endsWith('.variable')) {
    const name = typeof config['variableName'] === 'string' ? config['variableName'] : 'variable';
    return { status: 'success', output: { [name]: config['value'] } };
  }
  if (type.endsWith('.expression') || type.endsWith('.mapper')) {
    const expr = typeof config['expression'] === 'string' ? config['expression'] : undefined;
    if (expr) {
      try {
        return {
          status: 'success',
          output: { expressionResult: context.expressions.evaluate(expr, context.payload) },
        };
      } catch (error) {
        return {
          status: 'failure',
          message: error instanceof Error ? error.message : 'Expression failed',
        };
      }
    }
  }
  if (type.endsWith('.formatter')) {
    const inputField =
      typeof config['inputField'] === 'string' ? config['inputField'] : undefined;
    const pattern =
      typeof config['formatPattern'] === 'string' ? config['formatPattern'] : undefined;
    if (inputField) {
      const raw = context.payload[inputField];
      return {
        status: 'success',
        output: { [inputField]: pattern ? String(pattern).replace('{value}', String(raw ?? '')) : raw },
      };
    }
  }
  if (type.endsWith('.logger')) {
    return {
      status: 'success',
      output: {
        lastLog: {
          level: config['level'] ?? 'info',
          message: config['message'],
          at: new Date().toISOString(),
        },
      },
    };
  }
  if (type.endsWith('.environment')) {
    const key = typeof config['envKey'] === 'string' ? config['envKey'] : 'NODE_ENV';
    return { status: 'success', output: { [key]: 'browser' } };
  }
  if (type.endsWith('.secret')) {
    return { status: 'success', output: { secretResolved: false } };
  }
  // encrypt/decrypt/json/xml/csv/excel — passthrough stubs
  return { status: 'success', output: { utilityNode: type } };
};

export const executeWorkflowNode: CategoryExecutor = async (node, context) => {
  const type = node.nodeType.toLowerCase();
  const config = node.configuration ?? {};
  if (type.endsWith('.invoke-workflow') || type.endsWith('.reusable-workflow') || type.endsWith('.start-workflow')) {
    const workflowId =
      typeof config['workflowId'] === 'string' ? config['workflowId'] : undefined;
    return {
      status: 'success',
      output: {
        invokedWorkflowId: workflowId,
        subWorkflowRequested: true,
      },
    };
  }
  return { status: 'success', output: { workflowMeta: config } };
};

export const executeHumanInteractionNode: CategoryExecutor = () => ({ status: 'pause' });

export const executeDataDisplayNode: CategoryExecutor = () => ({ status: 'pause' });
